import type { RecentTradeMatchRequestDto } from '@utility-nyc/react-query-sdk';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { matchRecentEndUserTrade } from '@utility-nyc/react-query-sdk';

type RecentTradeMatchState = {
  requestKey: string;
  isChecking: boolean;
  isError: boolean;
  tradeRequestId?: string;
  retryAfterMs: number | null;
};

const initialState: RecentTradeMatchState = {
  requestKey: '',
  isChecking: true,
  isError: false,
  retryAfterMs: null,
};

const useRecentTradeMatch = (
  request: RecentTradeMatchRequestDto | null,
): RecentTradeMatchState => {
  const requestKey = useMemo(() => JSON.stringify(request), [request]);
  const stableRequest = useMemo<RecentTradeMatchRequestDto | null>(
    () => JSON.parse(requestKey) as RecentTradeMatchRequestDto | null,
    [requestKey],
  );
  const [state, setState] = useState<RecentTradeMatchState>(initialState);
  const currentRequestKey = useRef(requestKey);
  const requestSequence = useRef(0);

  currentRequestKey.current = requestKey;

  const refresh = useCallback(async () => {
    const sequence = ++requestSequence.current;

    if (!stableRequest) {
      setState({ ...initialState, requestKey, isChecking: false });

      return;
    }

    setState((current) =>
      current.requestKey === requestKey
        ? { ...current, isChecking: true, isError: false }
        : {
            requestKey,
            isChecking: true,
            isError: false,
            retryAfterMs: null,
          },
    );

    try {
      const result = await matchRecentEndUserTrade(stableRequest);

      if (
        currentRequestKey.current !== requestKey ||
        requestSequence.current !== sequence
      ) {
        return;
      }

      setState({
        requestKey,
        isChecking: false,
        isError: false,
        tradeRequestId: result.data.tradeRequestId,
        retryAfterMs: result.data.retryAfterMs,
      });
    } catch {
      if (
        currentRequestKey.current !== requestKey ||
        requestSequence.current !== sequence
      ) {
        return;
      }

      setState({
        requestKey,
        isChecking: false,
        isError: true,
        retryAfterMs: null,
      });
    }
  }, [requestKey, stableRequest]);

  useEffect(() => {
    refresh().catch(() => undefined);
  }, [refresh]);

  useEffect(() => {
    if (
      state.retryAfterMs === null ||
      state.retryAfterMs <= 0 ||
      state.isChecking
    ) {
      return;
    }

    const timeout = setTimeout(() => {
      refresh().catch(() => undefined);
    }, state.retryAfterMs);

    return () => clearTimeout(timeout);
  }, [refresh, state.isChecking, state.retryAfterMs]);

  return {
    ...state,
    isChecking: state.isChecking || state.requestKey !== requestKey,
  };
};

export { useRecentTradeMatch };
